import { type OptionsObject, type SnackbarKey, useSnackbar } from 'notistack';
import { useMemo } from 'react';

type NotifyOptions = Omit<OptionsObject, 'variant'>;

/** Handle to a shown notification, for the rare one that must be dismissed by code. */
export type NotificationKey = SnackbarKey;

export interface Notify {
  success: (message: string, options?: NotifyOptions) => NotificationKey;
  error: (message: string, options?: NotifyOptions) => NotificationKey;
  warning: (message: string, options?: NotifyOptions) => NotificationKey;
  info: (message: string, options?: NotifyOptions) => NotificationKey;
  /** Dismiss one notification by its key, or all of them when called with none. */
  dismiss: (key?: NotificationKey) => void;
}

/**
 * Small, stable wrapper around `notistack` that exposes a typed
 * surface for the app.
 *
 * Feature code should only ever import `useNotify()` — never
 * `useSnackbar()` directly — so the underlying implementation can be
 * replaced without touching callers.
 */
export function useNotify(): Notify {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return useMemo<Notify>(
    () => ({
      success: (message, options) => enqueueSnackbar(message, { ...options, variant: 'success' }),
      error: (message, options) => enqueueSnackbar(message, { ...options, variant: 'error' }),
      warning: (message, options) => enqueueSnackbar(message, { ...options, variant: 'warning' }),
      info: (message, options) => enqueueSnackbar(message, { ...options, variant: 'info' }),
      dismiss: (key) => {
        closeSnackbar(key);
      },
    }),
    [closeSnackbar, enqueueSnackbar],
  );
}
